import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';
import { getDaysBetweenDates } from '@ourbranch/date-fns-helpers';
import Grid from '@material-ui/core/Grid';

import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import { brandConfig } from 'brand-config';
import Field from 'core/components/form/form.v2';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleListItem from './collapsible-list-item';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';

import useStyles from './sub-risk.styles';

const validationSchema = () =>
  Yup.object().shape({
    category: Yup.string().required('Type of item is required'),
    name: Yup.string().required('Name is required'),
    limit: Yup.number().required('Coverage limit is required'),
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required').min(Yup.ref('startDate'), 'End date must be after start date')
  });

const PeakSeasonInventoryForm = ({ fieldName, disabled, state }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const peakSeasonItems =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.PEAK)?.items || [];

  const availableCategories =
    brandConfig?.farm[state]?.FO6Scheduled?.peakSeason?.itemTypes ||
    lookupsJson.farm.scheduledFarmPropertyItemTypesByState[state];

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Peak Season Inventory</div>}
        initialOpen={false}
      >
        <FormSectionWrapper
          sectionTitle="Peak season inventory items"
          totalLabel="items"
          totalCount={peakSeasonItems?.length || 0}
          emptyStateTitle="There are no peak season inventory items added yet"
        >
          {/* list items */}
          {peakSeasonItems.map((item, index) => {
            const itemCategory = item.category.slice(0, 1).toUpperCase() + item.category.slice(1);

            return (
              <CollapsibleListItem
                key={`peakSeasonInventory-${index}`}
                title={itemCategory}
                rightSideValues={[{ label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` }]}
                detailFields={[
                  { name: 'category', label: 'Type of Item', value: itemCategory, size: 4 },
                  { name: 'name', label: 'Name', value: item.extendedDetails?.name, size: 4 },
                  {
                    name: 'coverageLimit',
                    label: 'Coverage Limit',
                    value: item.limit ? `$${item.limit.toLocaleString('en-US')}` : null,
                    size: 4
                  },
                  { name: 'startDate', label: 'Start Date', value: item.extendedDetails?.startDate, size: 4 },
                  { name: 'endDate', label: 'End Date', value: item.extendedDetails?.endDate, size: 4 },
                  {
                    name: 'days',
                    label: 'Number of Days (calculated)',
                    value: getDaysBetweenDates(
                      new Date(item.extendedDetails?.startDate),
                      new Date(item.extendedDetails?.endDate)
                    )?.toString(),
                    size: 4
                  }
                ]}
                onDelete={() => {
                  const newItems = peakSeasonItems.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.PEAK) ||
                    [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.PEAK,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.PEAK}`);
                }}
                disabled={disabled}
                keyPrefix={`peakSeasonInventory-${index}`}
              />
            );
          })}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                category: undefined,
                name: undefined,
                limit: undefined,
                startDate: undefined,
                endDate: undefined,
                days: 'N/A',
                description: undefined
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema()}
              onSubmit={async (val, { resetForm }) => {
                const parentErrors = await validateForm();
                if (Object.keys(parentErrors).length > 0) {
                  setErrors(parentErrors);
                  setTouched(setTouchedFromErrors(parentErrors));
                  setShowValidationErrorsModal(true);
                  return;
                }

                const item = val;
                Object.keys(item).forEach((key) => {
                  if (item[key] === '') {
                    item[key] = null;
                  }
                });

                const newItem = {
                  category: item.category,
                  limit: item.limit,
                  extendedDetails: {
                    name: item.name,
                    startDate: item.startDate,
                    endDate: item.endDate
                  }
                };

                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.PEAK) || [];
                const currentPeakSeasonItems =
                  getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.PEAK)
                    ?.items || [];

                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.PEAK,
                    items: [...currentPeakSeasonItems, newItem]
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.PEAK}`);

                resetForm();
              }}
            >
              {({ values: formValues, setFieldValue: setFormFieldValue, submitForm }) => {
                // Calculate number of days whenever start or end date changes
                useEffect(() => {
                  if (formValues.startDate && formValues.endDate) {
                    const days = getDaysBetweenDates(new Date(formValues.startDate), new Date(formValues.endDate));
                    setFormFieldValue('days', days.toString());
                  } else {
                    setFormFieldValue('days', 'N/A');
                  }
                }, [formValues.startDate, formValues.endDate, setFormFieldValue]);

                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Field
                      xs={4}
                      type="select"
                      id="category"
                      name="category"
                      mode="dark"
                      label="Type of item"
                      options={availableCategories}
                      disabled={disabled}
                    />
                    <Field xs={4} type="string" id="name" name="name" mode="dark" label="Name" disabled={disabled} />
                    <Field
                      xs={4}
                      type="numeric"
                      id="coverageLimit"
                      name="limit"
                      mode="dark"
                      label="Coverage limit"
                      format={{ thousandSeparator: true, prefix: '$' }}
                      disabled={disabled}
                    />

                    <Field
                      xs={4}
                      type="date"
                      id="startDate"
                      name="startDate"
                      mode="dark"
                      label="Start date"
                      disabled={disabled}
                      defaultToEmpty
                    />
                    <Field
                      xs={4}
                      type="date"
                      id="endDate"
                      name="endDate"
                      mode="dark"
                      label="End date"
                      disabled={disabled}
                      defaultToEmpty
                    />
                    <Field
                      xs={4}
                      type="value"
                      id="days"
                      name="days"
                      mode="dark"
                      label="Number of days (calculated)"
                      disabled={true}
                    />

                    <Grid container alignItems="center" className={classes.singleBottom}>
                      <Button
                        type="button"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disabled}
                        onClick={submitForm}
                      >
                        Add Item
                      </Button>
                    </Grid>
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </>
  );
};

PeakSeasonInventoryForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

PeakSeasonInventoryForm.defaultProps = {
  disabled: false
};

export default withDatePicker(observer(PeakSeasonInventoryForm));
